import React, { useState, useEffect, useCallback } from 'react';
import SyncIcon from '@mui/icons-material/Sync';
import RefreshIcon from '@mui/icons-material/Refresh';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import ScheduleIcon from '@mui/icons-material/Schedule';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import api from '../../services/apiConfig';
import styles from './AdminSyncWorkerPage.module.css';

interface SyncStatus {
    enabled: boolean;
    running: boolean;
    lastSyncTime: string | null;
    lastSyncStatus: 'success' | 'error' | 'idle';
    lastSyncMessage: string;
    pollingIntervalMs: number;
}

interface ToastData {
    type: 'success' | 'error' | 'info';
    message: string;
}

interface AdminSyncWorkerPageProps {
    navbar: React.ReactNode;
}

const AdminSyncWorkerPage: React.FC<AdminSyncWorkerPageProps> = ({ navbar }) => {
    const [status, setStatus] = useState<SyncStatus | null>(null);
    const [loading, setLoading] = useState(true);
    const [toggling, setToggling] = useState(false);
    const [syncing, setSyncing] = useState(false);
    const [toast, setToast] = useState<ToastData | null>(null);

    const showToast = (type: ToastData['type'], message: string) => {
        setToast({ type, message });
        setTimeout(() => setToast(null), 5000);
    };

    const fetchStatus = useCallback(async () => {
        try {
            const res = await api.get('/sync-worker/status');
            setStatus(res.data);
        } catch (err: any) {
            console.error('Erro ao buscar status do SyncWorker:', err);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchStatus();
        const interval = setInterval(fetchStatus, 5000);
        return () => clearInterval(interval);
    }, [fetchStatus]);

    const handleToggle = async () => {
        if (!status || toggling) return;
        setToggling(true);
        try {
            const endpoint = status.enabled ? '/sync-worker/disable' : '/sync-worker/enable';
            const res = await api.post(endpoint);
            showToast('success', res.data.message);
            await fetchStatus();
        } catch (err: any) {
            showToast('error', 'Erro ao alterar estado do SyncWorker.');
        } finally {
            setToggling(false);
        }
    };

    const handleTrigger = async () => {
        if (syncing) return;
        setSyncing(true);
        showToast('info', 'Sincronização manual iniciada...');
        try {
            const res = await api.post('/sync-worker/trigger');
            if (res.data.triggered) {
                showToast('success', res.data.message);
            } else {
                showToast('info', res.data.message);
            }
            await fetchStatus();
        } catch (err: any) {
            showToast('error', 'Erro ao executar sincronização manual.');
        } finally {
            setSyncing(false);
        }
    };

    const formatDate = (iso: string | null) => {
        if (!iso) return '—';
        const d = new Date(iso);
        return d.toLocaleString('pt-BR', {
            day: '2-digit', month: '2-digit', year: 'numeric',
            hour: '2-digit', minute: '2-digit', second: '2-digit',
        });
    };

    const getStatusLabel = () => {
        if (!status) return 'Carregando...';
        if (status.running || syncing) return 'Sincronizando...';
        if (status.enabled) return 'Ativo';
        return 'Desativado';
    };

    const getStatusClass = () => {
        if (!status) return styles.statusUnknown;
        if (status.running || syncing) return styles.statusRunning;
        if (status.enabled) return styles.statusActive;
        return styles.statusInactive;
    };

    return (
        <div className={styles.wrapper}>
            {navbar}

            <div className={styles.card}>
                <div className={styles.cardHeader}>
                    <h3 className={styles.cardTitle}>Status do Sincronizador</h3>
                    <div className={styles.actions}>
                        <button
                            className={`${styles.actionBtn} ${styles.syncBtn}`}
                            onClick={handleTrigger}
                            disabled={syncing || !status}
                        >
                            <SyncIcon className={syncing ? styles.spinning : ''} />
                            {syncing ? 'Sincronizando...' : 'Sincronizar Agora'}
                        </button>
                        <button
                            className={`${styles.actionBtn} ${styles.refreshBtn}`}
                            onClick={fetchStatus}
                            disabled={loading}
                        >
                            <RefreshIcon />
                            Atualizar
                        </button>
                    </div>
                </div>

                <div className={styles.tableWrapper}>
                    <table className={styles.table}>
                        <tbody>
                            <tr>
                                <td className={styles.labelCell}>Estado Automático</td>
                                <td>
                                    <div className={styles.toggleRow}>
                                        <span className={`${styles.statusBadge} ${getStatusClass()}`}>
                                            {getStatusLabel()}
                                        </span>
                                        <button 
                                            className={`${styles.toggleBtn} ${status?.enabled ? styles.toggleOn : styles.toggleOff}`}
                                            onClick={handleToggle}
                                            disabled={toggling || loading}
                                        >
                                            {status?.enabled ? 'Desligar Sincronização' : 'Ligar Sincronização'}
                                        </button>
                                    </div>
                                </td>
                            </tr>
                            <tr>
                                <td className={styles.labelCell}>Última Sincronização</td>
                                <td>{loading ? 'Carregando...' : formatDate(status?.lastSyncTime ?? null)}</td>
                            </tr>
                            <tr>
                                <td className={styles.labelCell}>Resultado</td>
                                <td>
                                    {loading ? 'Carregando...' : (
                                        <div className={styles.resultRow}>
                                            {status?.lastSyncStatus === 'success' && <CheckCircleOutlineIcon className={styles.resultSuccess} />}
                                            {status?.lastSyncStatus === 'error' && <ErrorOutlineIcon className={styles.resultError} />}
                                            {status?.lastSyncStatus === 'idle' && <ScheduleIcon className={styles.resultIdle} />}
                                            <span className={styles[`text${status?.lastSyncStatus}`]}>
                                                {status?.lastSyncStatus === 'success' ? 'Sucesso' : status?.lastSyncStatus === 'error' ? 'Falha' : 'Aguardando'}
                                            </span>
                                        </div>
                                    )}
                                </td>
                            </tr>
                            <tr>
                                <td className={styles.labelCell}>Mensagem do Sistema</td>
                                <td>{loading ? 'Carregando...' : (status?.lastSyncMessage || '—')}</td>
                            </tr>
                            <tr>
                                <td className={styles.labelCell}>Intervalo (Polling)</td>
                                <td>{loading ? 'Carregando...' : `${(status?.pollingIntervalMs ?? 0) / 1000} segundos`}</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>

            {toast && (
                <div className={`${styles.toast} ${
                    toast.type === 'success' ? styles.toastSuccess :
                    toast.type === 'error' ? styles.toastError :
                    styles.toastInfo
                }`}>
                    {toast.type === 'success' && <CheckCircleOutlineIcon />}
                    {toast.type === 'error' && <ErrorOutlineIcon />}
                    {toast.type === 'info' && <InfoOutlinedIcon />}
                    {toast.message}
                </div>
            )}
        </div>
    );
};

export default AdminSyncWorkerPage;
